import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Building2, Loader2, ShieldAlert } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { adminApi } from "../../lib/api";

/**
 * Sponsor Portal entrypoint.
 *   - Unauthenticated → /sponsor/login
 *   - Signed in and a sponsor_admin of exactly one sponsor → jump to that
 *     sponsor's dashboard.
 *   - Signed in with multiple sponsor bindings → picker.
 *   - Signed in but NOT a sponsor admin → Access Denied. Master admins are
 *     deliberately NOT auto-forwarded to /admin; they must open /admin
 *     explicitly. This prevents shoulder-surfing / discoverability.
 */
export default function SponsorPortalHome() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ checking: true, bindings: [] });

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    adminApi
      .whoami()
      .then((r) => setState({ checking: false, bindings: r?.sponsor_admin_bindings || [] }))
      .catch(() => setState({ checking: false, bindings: [] }));
  }, [user, loading]);

  if (loading || (user && state.checking)) {
    return (
      <div className="min-h-screen bg-[#0B1210] text-[#B7D2AF] flex items-center justify-center">
        <Loader2 size={16} className="animate-spin mr-2" /> Checking sponsor access…
      </div>
    );
  }

  if (!user) return <Navigate to="/sponsor/login" replace />;

  if (state.bindings.length === 1) {
    return <Navigate to={`/sponsor-dashboard/${state.bindings[0].sponsor_id}`} replace />;
  }

  if (state.bindings.length === 0) {
    return (
      <div className="min-h-screen bg-[#0B1210] flex items-center justify-center px-6">
        <div
          data-testid="sponsor-portal-denied"
          className="max-w-md w-full rounded-3xl border border-[#7C3E33]/50 bg-[#1A100E] p-8 text-center"
        >
          <ShieldAlert size={20} className="text-[#E38680] mx-auto mb-3" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#E38680] mb-2">
            Access Denied
          </div>
          <h1 className="font-display text-2xl text-white mb-2">
            Your account is not a sponsor admin
          </h1>
          <p className="text-sm text-[#B7D2AF] leading-relaxed mb-6">
            Signed in as <span className="text-white">{user.email}</span>. If
            you should have access to a sponsor organization, please check
            your invitation email or contact the LifeGuide team.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="sponsor-portal-signout"
              onClick={() => {
                logout();
                navigate("/sponsor/login");
              }}
              className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-2.5 text-sm font-medium"
            >
              Sign in with a different account
            </button>
            <a href="/" className="text-sm text-[#B7D2AF] hover:text-white">
              Back to LifeGuide
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="sponsor-portal-picker"
      className="min-h-screen bg-[#0B1210] text-white px-6 py-12"
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2.5 mb-6">
          <span className="inline-flex w-9 h-9 rounded-xl bg-[#4F6F52]/20 items-center justify-center">
            <Building2 size={16} className="text-[#B7D2AF]" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#B7D2AF]">
              Sponsor Portal
            </div>
            <div className="font-display text-xl">Choose an organization</div>
          </div>
        </div>
        <ul className="space-y-3">
          {state.bindings.map((b) => (
            <li key={b.sponsor_id}>
              <a
                href={`/sponsor-dashboard/${b.sponsor_id}`}
                data-testid={`sponsor-picker-${b.sponsor_id}`}
                className="block rounded-2xl border border-white/10 bg-[#141B18] hover:bg-[#1B2420] px-5 py-4 transition"
              >
                <div className="text-sm font-semibold text-white">
                  {b.sponsor_name || b.sponsor_id}
                </div>
                <div className="text-[11px] text-[#7C8F7C] mt-0.5">
                  {b.sponsor_type || "Sponsor"}
                </div>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
